import {
  PriorYearPricingInput,
  getIsJanuaryFirstCoverage,
  getIsPreviousYearSupported,
} from './priorYearPricing';

/**
 * Renewal mode prices a hidden baseline scenario off last year's plan design,
 * which only makes sense for a January 1st continuation of a census that names
 * the plans its employees are already on.
 *
 * The backend turns each violation into its own error and the new-quote wizard
 * uses them to gate the renewal entry, so both read this one list rather than
 * each keeping their own copy of the conditions. Holding a single carried-over
 * design is not here: the wizard's panel is structurally single-selection, so
 * only the backend has a count to check.
 */
export enum RenewalTriggerViolation {
  CENSUS_WITHOUT_PLAN_IDS = 'CENSUS_WITHOUT_PLAN_IDS',
  COVERAGE_NOT_JANUARY_FIRST = 'COVERAGE_NOT_JANUARY_FIRST',
  PREVIOUS_YEAR_UNSUPPORTED = 'PREVIOUS_YEAR_UNSUPPORTED',
}

export type RenewalTriggerInput = PriorYearPricingInput & {
  censusUsesPlanIds: boolean;
};

export const getRenewalTriggerViolation = ({
  censusUsesPlanIds,
  startOfCoverage,
  quotingYear,
}: RenewalTriggerInput): RenewalTriggerViolation | null => {
  if (!censusUsesPlanIds) {
    return RenewalTriggerViolation.CENSUS_WITHOUT_PLAN_IDS;
  }

  if (!getIsJanuaryFirstCoverage({ startOfCoverage, quotingYear })) {
    return RenewalTriggerViolation.COVERAGE_NOT_JANUARY_FIRST;
  }

  // The baseline is priced on the year before, so that year's rates have to be
  // among the ones we support
  if (!getIsPreviousYearSupported(quotingYear)) {
    return RenewalTriggerViolation.PREVIOUS_YEAR_UNSUPPORTED;
  }

  return null;
};

export const getIsRenewalTriggerMet = (input: RenewalTriggerInput): boolean =>
  getRenewalTriggerViolation(input) === null;
